/**
 * `/faq` — the questions a signed-out visitor asks before they make an account.
 *
 * One decision shaped it: **every published question is on this page at once, and each one has
 * a URL.** Support answers a ticket by pasting `/faq#q-<id>`, so the entry the link names has to
 * open and scroll itself into view on arrival — not merely exist somewhere on a long page. That
 * is why the entries are `<details>` elements rather than a hand-rolled accordion: the browser
 * already knows how to open one, keyboard and screen reader included, and `open` is a property
 * a deep link can set without touching any state of ours.
 *
 * There is no category rail, because there are no categories: `faq_entries` carries
 * `{ id, question, answer, sortOrder, active }` and nothing else (confirmed against the live dev
 * API, 2026-09-10). The endpoint's `sortOrder` is the whole editorial control an admin has, so
 * the page renders exactly that order and adds no opinion of its own.
 *
 * An empty CMS renders a line and the Play free CTA. A visitor who followed a link to the FAQ and
 * got a blank screen learns nothing about the game; one who gets "no questions yet, here is the
 * queue" learns the only thing that matters.
 */
import { t } from '../i18n';
import { icon, plateChrome, type PlateChromeOptions } from '../shell/ui';
import { landingFooter, landingTopBar, pageTitle } from './landing';
import { faqAnchorId, fetchFaqEntries, isAborted, sanitizeCmsHtml, type FaqEntry } from './content-api';

type Navigate = (to: string) => void;

const H1 = 'kit-title font-heading uppercase text-[24px] sm:text-[36px] lg:text-[46px] m-0';
const LEDE = 'text-[15px] sm:text-[17px] leading-relaxed text-text-dim max-w-[62ch] m-0';
const CTA_PRIMARY: PlateChromeOptions = { variant: 'gold', size: 'lg', className: 'text-[15px] tracking-[0.12em]' };
const CTA_GHOST: PlateChromeOptions = { variant: 'secondary', size: 'lg', className: 'text-[15px] tracking-[0.12em]' };

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className = '', text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function plateLink(href: string, label: string | Node, opts: PlateChromeOptions, navigate: Navigate): HTMLAnchorElement {
  const a = document.createElement('a');
  a.href = href;
  plateChrome(a, label, { ...opts, className: `no-underline ${opts.className ?? ''}` });
  a.addEventListener('click', (event) => {
    event.preventDefault();
    navigate(href);
  });
  return a;
}

function entryRow(entry: FaqEntry, index: number): HTMLDetailsElement {
  const row = el(
    'details',
    `reveal group kit-plate [--kp-rim:16px] [--kp-pad:6px_14px] scroll-mt-28 [transition-delay:${Math.min(index, 6) * 50}ms]`,
  );
  row.id = faqAnchorId(entry);
  row.dataset.slot = 'faq-entry';

  const head = el(
    'summary',
    'flex items-center justify-between gap-4 min-h-[56px] py-2 cursor-pointer list-none [touch-action:manipulation] marker:hidden [&::-webkit-details-marker]:hidden',
  );
  head.append(
    el(
      'span',
      'kit-label font-heading uppercase text-[14px] sm:text-[15px] leading-snug',
      entry.question,
    ),
  );
  // The painted + turns into a × when the entry is open.
  const chevron = el('span', 'shrink-0 transition-transform duration-200 motion-reduce:transition-none group-open:rotate-45');
  chevron.append(icon('add', { size: 40 }));
  chevron.setAttribute('aria-hidden', 'true');
  head.append(chevron);

  const body = el(
    'div',
    'kit-well mb-2 px-4 py-3 text-[14px] sm:text-[15px] leading-relaxed text-text [&_p]:m-0 [&_p+p]:mt-3 [&_a]:text-cyan [&_ul]:pl-5 [&_ul]:my-2 [&_li]:list-disc [&_strong]:text-text [&_pre]:overflow-x-auto',
  );
  // The answer is admin-authored HTML: sanitised, then set as markup so lists and links survive.
  body.innerHTML = sanitizeCmsHtml(entry.answer ?? '');
  row.append(head, body);
  return row;
}

function skeleton(): HTMLElement {
  const wrap = el('div', 'flex flex-col gap-3');
  wrap.dataset.slot = 'faq-loading';
  wrap.setAttribute('aria-busy', 'true');
  for (let i = 0; i < 5; i += 1) wrap.append(el('div', 'kit-plate [--kp-rim:16px] h-[64px] opacity-60 motion-safe:animate-pulse'));
  return wrap;
}

function titlePlate(title: string): HTMLElement {
  const plate = el('div', 'kit-header [--kh:46px] w-fit max-w-full');
  plate.append(el('h2', 'kit-title font-heading uppercase text-[16px] m-0', title));
  return plate;
}

function noticeBlock(title: string, line: string, actions: Node[], slot: string): HTMLElement {
  const box = el('div', 'kit-window flex flex-col items-start gap-4');
  box.dataset.slot = slot;
  box.append(
    titlePlate(title),
    el('p', 'text-[15px] leading-relaxed text-text-dim max-w-[52ch] m-0', line),
  );
  if (actions.length) {
    const row = el('div', 'flex flex-wrap gap-3');
    row.append(...actions);
    box.append(row);
  }
  return box;
}

export function renderFaqPage(app: HTMLElement, navigate: Navigate): () => void {
  const reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  const root = el('div', 'landing relative min-h-full bg-bg text-text overflow-x-hidden');
  root.dataset.view = 'faq';

  const main = el(
    'main',
    'relative px-5 sm:px-8 lg:px-14 xl:px-24 pt-[calc(96px+env(safe-area-inset-top))] pb-20 lg:pb-28 max-w-[900px] mx-auto',
  );

  const head = el('header', 'flex flex-col gap-4 mb-10 lg:mb-14');
  head.append(pageTitle('bell', el('h1', H1, t('nav.questions'))), el('p', LEDE, t('faq.lede')));
  main.append(head);

  const column = el('div', 'flex flex-col gap-3 min-w-0');
  column.dataset.slot = 'faq-body';
  column.append(skeleton());
  main.append(column);

  const tail = el('div', 'flex flex-col gap-4 pt-12');
  tail.dataset.slot = 'faq-tail';
  main.append(tail);

  root.append(landingTopBar(navigate), main, landingFooter(navigate));
  app.replaceChildren(root);
  try {
    window.scrollTo(0, 0);
  } catch {
    /* jsdom */
  }

  // ── Scroll reveal, same idiom as the landing: instant with no observer or under reduced
  //    motion, because a reveal that never fires is a page with missing content.
  let observer: IntersectionObserver | null = null;
  if (!reducedMotion && typeof IntersectionObserver === 'function') {
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            observer?.unobserve(entry.target);
          }
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.08 },
    );
  }
  const revealIn = (scope: HTMLElement): void => {
    for (const node of scope.querySelectorAll<HTMLElement>('.reveal')) {
      if (observer) observer.observe(node);
      else node.classList.add('is-in');
    }
  };

  /** Opens the entry a `#q-<id>` hash names and puts it on screen. Also runs on `hashchange`. */
  const openFromHash = (smooth: boolean): void => {
    const hash = window.location.hash.replace(/^#/, '');
    if (!hash) return;
    // `CSS.escape` is missing in some jsdom versions and the ids are cuids anyway, so the
    // fallback is a plain lookup rather than a thrown selector error.
    const escaped = typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(hash) : hash;
    let target: HTMLElement | null = null;
    try {
      target = root.querySelector<HTMLElement>(`#${escaped}`);
    } catch {
      target = null;
    }
    if (!target) return;
    if (target instanceof HTMLDetailsElement) target.open = true;
    target.classList.add('is-in');
    try {
      target.scrollIntoView({ behavior: smooth && !reducedMotion ? 'smooth' : 'auto', block: 'start' });
    } catch {
      /* jsdom */
    }
  };
  const onHashChange = (): void => openFromHash(true);
  window.addEventListener('hashchange', onHashChange);

  const controller = new AbortController();
  void (async () => {
    try {
      const entries = await fetchFaqEntries({ signal: controller.signal });
      if (controller.signal.aborted) return;
      if (entries.length === 0) {
        column.replaceChildren(
          noticeBlock(
            t('faq.emptyTitle'),
            t('faq.emptyBody'),
            [plateLink('/register', t('landing.hero.playFree'), CTA_PRIMARY, navigate), plateLink('/guide', t('faq.readGuide'), CTA_GHOST, navigate)],
            'faq-empty',
          ),
        );
        return;
      }
      column.replaceChildren(...entries.map(entryRow));
      tail.replaceChildren(
        el(
          'p',
          'text-[14px] leading-relaxed text-text-dim m-0',
          t('faq.stillStuck'),
        ),
        (() => {
          const row = el('div', 'flex flex-wrap gap-3');
          row.append(plateLink('/guide', t('faq.readGuide'), CTA_GHOST, navigate), plateLink('/register', t('landing.hero.playFree'), CTA_PRIMARY, navigate));
          return row;
        })(),
      );
      revealIn(column);
      openFromHash(false);
    } catch (error) {
      if (isAborted(error) || controller.signal.aborted) return;
      column.replaceChildren(
        noticeBlock(
          t('faq.offlineTitle'),
          t('faq.offlineBody'),
          [plateLink('/register', t('landing.hero.playFree'), CTA_PRIMARY, navigate)],
          'faq-error',
        ),
      );
    }
  })();

  return () => {
    controller.abort();
    observer?.disconnect();
    window.removeEventListener('hashchange', onHashChange);
    root.remove();
  };
}
